import { observable, action, reaction, toJS, computed } from 'mobx';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

var MUST_BE_POPULATED = function (value) {
    var isString = typeof value === 'string';
    return isString ? value.length > 0 : value !== undefined;
};
var MUST_BE_TRUE = function (value) {
    return value === true;
};
var MUST_BE_BOOL = function (value) {
    return value === true || value === false;
};
var MUST_BE_STRING = function (value) {
    return typeof value === 'string';
};
var MUST_BE_NUMBER_PRIMITIVE = function (value) {
    return typeof value === 'number';
};
var MUST_BE_NUMBER = function (value) {
    return !isNaN(value);
};
var MUST_BE_EMAIL = function (value) {
    var validEmailPattern = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
    return validEmailPattern.test(value);
};
var MIN_LENGTH = function (length) { return function (value) {
    return value.length >= length;
}; };
var MAX_LENGTH = function (length) { return function (value) {
    return value.length <= length;
}; };

var GenericFormInputPresenter = /** @class */ (function () {
    function GenericFormInputPresenter(initialValue) {
        var _this = this;
        this.isValid = true;
        this.errorMessages = [];
        this.isDirty = false;
        this.disabled = false;
        this.required = false;
        this.rules = [];
        this.middleware = [];
        this.finishedChangeCycle = null;
        this.onChange = function (newValue) {
            if (!_this.disabled) {
                _this.value = _this.middleware.reduce(function (accumulator, middleware) { return middleware(accumulator); }, newValue);
                if (_this.finishedChangeCycle)
                    _this.finishedChangeCycle();
                _this.isDirty = true;
            }
        };
        this.withMiddleware = function (middleware) {
            _this.middleware.push(middleware);
            return _this;
        };
        this.reset = function () {
            _this.value = _this.initialValue;
            _this.isDirty = false;
        };
        this.addCustomRule = function (condition, errorMessage) {
            if (errorMessage == '')
                throw new Error('Validation provided without empty message');
            _this.rules.push({ condition: condition, errorMessage: errorMessage });
            _this.validate();
            return _this;
        };
        this.mustBeEmail = function (errorMessage) {
            return _this.addCustomRule(MUST_BE_EMAIL, errorMessage);
        };
        this.mustBeTrue = function (errorMessage) {
            return _this.addCustomRule(MUST_BE_TRUE, errorMessage);
        };
        this.mustBeBool = function (errorMessage) {
            return _this.addCustomRule(MUST_BE_BOOL, errorMessage);
        };
        this.mustBeString = function (errorMessage) {
            return _this.addCustomRule(MUST_BE_STRING, errorMessage);
        };
        this.mustBeNumberPrimitive = function (errorMessage) {
            return _this.addCustomRule(MUST_BE_NUMBER_PRIMITIVE, errorMessage);
        };
        this.mustBeNumber = function (errorMessage) {
            return _this.addCustomRule(MUST_BE_NUMBER, errorMessage);
        };
        this.mustHaveMinLength = function (length, errorMessage) {
            return _this.addCustomRule(MIN_LENGTH(length), errorMessage);
        };
        this.mustHaveMaxLength = function (length, errorMessage) {
            return _this.addCustomRule(MAX_LENGTH(length), errorMessage);
        };
        this.isRequired = function (errorMessage) {
            _this.required = true;
            return _this.addCustomRule(MUST_BE_POPULATED, errorMessage);
        };
        this.isDisabled = function () {
            _this.disabled = true;
            return _this;
        };
        this.validate = function () {
            var isValid = true;
            var errorMessages = [];
            _this.rules.forEach(function (rule) {
                if (!rule.condition(_this.value)) {
                    isValid = false;
                    if (rule.errorMessage != undefined) {
                        errorMessages.push(rule.errorMessage);
                    }
                }
            });
            _this.isValid = isValid;
            _this.errorMessages = errorMessages;
        };
        this.initialValue = initialValue;
        this.value = initialValue;
        reaction(function () { return _this.value; }, this.validate);
    }
    __decorate([
        observable,
        __metadata("design:type", Object)
    ], GenericFormInputPresenter.prototype, "value", void 0);
    __decorate([
        observable,
        __metadata("design:type", Boolean)
    ], GenericFormInputPresenter.prototype, "isValid", void 0);
    __decorate([
        observable,
        __metadata("design:type", Array)
    ], GenericFormInputPresenter.prototype, "errorMessages", void 0);
    __decorate([
        observable,
        __metadata("design:type", Boolean)
    ], GenericFormInputPresenter.prototype, "isDirty", void 0);
    __decorate([
        observable,
        __metadata("design:type", Boolean)
    ], GenericFormInputPresenter.prototype, "disabled", void 0);
    __decorate([
        observable,
        __metadata("design:type", Boolean)
    ], GenericFormInputPresenter.prototype, "required", void 0);
    __decorate([
        observable,
        __metadata("design:type", Array)
    ], GenericFormInputPresenter.prototype, "rules", void 0);
    __decorate([
        action,
        __metadata("design:type", Object)
    ], GenericFormInputPresenter.prototype, "addCustomRule", void 0);
    __decorate([
        action,
        __metadata("design:type", Object)
    ], GenericFormInputPresenter.prototype, "validate", void 0);
    return GenericFormInputPresenter;
}());

var GenericFormPresenter = /** @class */ (function () {
    function GenericFormPresenter() {
        this.serverErrors = [];
        this.formInputs = [];
    }
    GenericFormPresenter.prototype.addFormInput = function (formInput, finishedChangeCycleArg) {
        formInput.finishedChangeCycle = finishedChangeCycleArg;
        this.formInputs.push(formInput);
        return this;
    };
    Object.defineProperty(GenericFormPresenter.prototype, "isDirty", {
        get: function () {
            return this.formInputs
                .map(function (input) { return input.isDirty; })
                .reduce(function (accumulator, currentValue) {
                if (accumulator)
                    return true;
                return currentValue;
            }, false);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(GenericFormPresenter.prototype, "isValid", {
        get: function () {
            if ((this.serverErrors || []).length > 0)
                return false;
            return this.formInputs
                .map(function (input) { return input.isValid; })
                .reduce(function (accumulator, currentValue) {
                if (!accumulator)
                    return false;
                return currentValue;
            }, true);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(GenericFormPresenter.prototype, "errorMessages", {
        get: function () {
            return (toJS(this.serverErrors) || []).concat(toJS(this.formInputs)
                .filter(function (input) {
                var hasBeenPopulated = !(input.value == '' && !input.isDirty);
                return hasBeenPopulated;
            })
                .reduce(function (flat, inputToFlatten) {
                return flat.concat(inputToFlatten.errorMessages);
            }, []));
        },
        enumerable: true,
        configurable: true
    });
    GenericFormPresenter.prototype.reset = function () {
        this.formInputs.forEach(function (input) {
            input.reset();
        });
        this.serverErrors = [];
    };
    __decorate([
        observable,
        __metadata("design:type", Array)
    ], GenericFormPresenter.prototype, "serverErrors", void 0);
    __decorate([
        observable,
        __metadata("design:type", Array)
    ], GenericFormPresenter.prototype, "formInputs", void 0);
    __decorate([
        computed,
        __metadata("design:type", Boolean),
        __metadata("design:paramtypes", [])
    ], GenericFormPresenter.prototype, "isDirty", null);
    __decorate([
        computed,
        __metadata("design:type", Boolean),
        __metadata("design:paramtypes", [])
    ], GenericFormPresenter.prototype, "isValid", null);
    __decorate([
        computed,
        __metadata("design:type", Array),
        __metadata("design:paramtypes", [])
    ], GenericFormPresenter.prototype, "errorMessages", null);
    __decorate([
        action,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], GenericFormPresenter.prototype, "reset", null);
    return GenericFormPresenter;
}());

export { GenericFormInputPresenter, GenericFormPresenter };
